import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"

import { TabTag } from "@/components/shared"
import { PopUpOrientation } from "@/components/shared/global/pop-up-orientation"
import { Button, Input, Label } from "@/components/ui"
import { AddressSection } from "@/features/jobs/explore/components/campaign"
import { ModalUpdateAddress } from "@/features/users/components/informations"
import { EKocCategory } from "@/modules/helpers/enum"
import { useAddresses } from "@/modules/helpers/hooks"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { getImageByNameFormatV2 } from "@/modules/helpers/utils"
import { formatAddress } from "@/modules/helpers/utils/campaign-form"
import type { Address } from "@/modules/types"

dayjs.extend(customParseFormat)
import { useQueryClient } from "@tanstack/react-query"
import { Card as CardIcon, Edit, Warning2 } from "iconsax-reactjs"
import { Check, Loader2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"

import { IdentityCardSheet } from "@/features/users/components/profile/identity-card-sheet"
import { ProfileEditModal } from "@/features/users/components/profile/profile-edit-modal"
import { useUpdateTaxCode } from "@/modules/helpers/hooks"

export function ProfileDetailsSection() {
  const { user } = useCurrentUser()
  const [isVisible, setIsVisible] = useState(false)
  const [showOrientationSheet, setShowOrientationSheet] = useState(false)
  const [showSheet, setShowSheet] = useState(false)
  const [isEditingTaxCode, setIsEditingTaxCode] = useState(false)
  const [taxCodeValue, setTaxCodeValue] = useState((user?.profiles as any)?.taxCode || "")
  const [isManageAddressVisible, setIsManageAddressVisible] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null)

  const queryClient = useQueryClient()
  const { mutate: updateTaxCode, isPending: isSavingTaxCode } = useUpdateTaxCode({
    onSuccess: () => {
      toast.success("Cập nhật mã số thuế thành công")
      setIsEditingTaxCode(false)
      queryClient.invalidateQueries({ queryKey: ["user_info"] })
    },
    onError: (error: any) => {
      toast.error(error.message || "Không thể cập nhật mã số thuế")
    }
  })

  // Sync taxCodeValue when user data loads
  const taxCode = (user?.profiles as any)?.taxCode
  useEffect(() => {
    setTaxCodeValue(taxCode || "")
  }, [taxCode])

  const handleSaveTaxCode = () => {
    updateTaxCode({ taxCode: taxCodeValue })
  }
  const [editingAddress, setEditingAddress] = useState<Address | null>(null)

  const { data: addressesResponse, refetch: refetchAddresses } = useAddresses()
  const addresses = useMemo(() => addressesResponse?.data || [], [addressesResponse?.data])

  // Initialize selectedAddress when addresses load
  useEffect(() => {
    if (!selectedAddress && addresses.length > 0) {
      const defaultAddress = addresses.find((addr: Address) => addr.isDefault) || addresses[0]
      if (defaultAddress) {
        setSelectedAddress(defaultAddress)
      }
    }
  }, [addresses, selectedAddress])

  const hasInfo = user?.profiles?.identifyNumber || user?.profiles?.dateGrantIdentify

  const handleAddressClick = useCallback((address: Address) => {
    setSelectedAddress(address)
  }, [])

  const handleEditAddress = useCallback((address: Address) => {
    setEditingAddress(address)
    setIsManageAddressVisible(true)
  }, [])

  const handleAddNewAddress = useCallback(() => {
    setEditingAddress(null)
    setIsManageAddressVisible(true)
  }, [])

  const handleAddressModalClose = useCallback(() => {
    setIsManageAddressVisible(false)
    setEditingAddress(null)
  }, [])

  const handleAddressCompleted = useCallback(() => {
    setIsManageAddressVisible(false)
    setEditingAddress(null)
    // Refetch to ensure list is updated
    refetchAddresses()
  }, [refetchAddresses])

  const getOrientationLabel = (category: EKocCategory) => {
    const labels: Record<EKocCategory, string> = {
      [EKocCategory.UNBOXING]: "Unboxing",
      [EKocCategory.REVIEW]: "Review trải nghiệm/Show mặt",
      [EKocCategory.GET_READY_WITH_ME]: "Get ready/Unready with me",
      [EKocCategory.SHOW_FULL_BODY]: "Show toàn thân",
      [EKocCategory.DUPE_OR_BETTER]: "Dupe or Better",
      [EKocCategory.DAY_IN_THE_LIFE]: "Day in the Life/ Mini vlog",
      [EKocCategory.TUTORIAL]: "Hướng dẫn sử dụng (Tutorial)"
    }
    return labels[category] || category
  }

  return (
    <div className="space-y-4 bg-background p-4 rounded-xl">
      {/* Tên tài khoản */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-normal text-gray-900">Tên tài khoản</Label>
          {!user?.username && <Warning2 size={14} className="text-red-500" />}
        </div>
        <div className="relative">
          <Input
            value={user?.username || ""}
            readOnly
            autoComplete="off"
            className="pr-10 bg-white rounded-xl border-0 h-12 text-sm"
            placeholder="Nhập tên tài khoản"
          />
          <button
            onClick={() => setIsVisible(true)}
            className="absolute right-4 top-1/2 -translate-y-1/2"
          >
            <Edit size={16} />
          </button>
        </div>
      </div>
      <ProfileEditModal isVisible={isVisible} setIsVisible={setIsVisible} />

      {/* Email */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-normal text-gray-900">Email</Label>
          {!user?.email && <Warning2 size={14} className="text-red-500" />}
        </div>
        <div className="relative">
          <Input
            value={user?.email || ""}
            readOnly
            autoComplete="off"
            className="pr-10 bg-white rounded-xl border-0 h-12 text-sm"
            placeholder="Nhập email"
          />
          <button
            onClick={() => setIsVisible(true)}
            className="absolute right-4 top-1/2 -translate-y-1/2"
          >
            <Edit size={16} />
          </button>
        </div>
      </div>

      {/* Số điện thoại */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-normal text-gray-900">Số điện thoại (Zalo)</Label>
          {!user?.phoneNumber && <Warning2 size={14} className="text-red-500" />}
        </div>
        <div className="relative">
          <Input
            value={user?.phoneNumber || ""}
            readOnly
            autoComplete="off"
            className="pr-10 bg-white rounded-xl border-0 h-12 text-sm"
            placeholder="Cần nhập số điện thoại"
          />
          <button
            onClick={() => setIsVisible(true)}
            className="absolute right-4 top-1/2 -translate-y-1/2"
          >
            <Edit size={16} />
          </button>
        </div>
      </div>

      {/* Địa chỉ nhận hàng */}
      <div className="space-y-2">
        <AddressSection
          addresses={addresses}
          selectedAddress={selectedAddress}
          onAddressClick={handleAddressClick}
          onEditClick={handleEditAddress}
          onAddNew={handleAddNewAddress}
          formatAddress={formatAddress}
          errors={{}}
          light
        />
        <ModalUpdateAddress
          visible={isManageAddressVisible}
          isPending={false}
          addressCount={addresses?.length || 0}
          onClose={handleAddressModalClose}
          onCompleted={handleAddressCompleted}
          addressId={editingAddress?.id}
          defaultValues={
            editingAddress
              ? {
                  address: editingAddress.address,
                  phoneNumber: editingAddress.phoneNumber,
                  fullName: editingAddress.fullName,
                  provinceCode: editingAddress.provinces?.code || "",
                  districtCode: editingAddress.districts?.code || "",
                  wardCode: editingAddress.wards?.code || "",
                  isDefault: editingAddress.isDefault
                }
              : undefined
          }
        />
      </div>

      {/* Định hướng */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-normal text-gray-900">Định hướng</Label>
          <button
            onClick={() => setShowOrientationSheet(true)}
            className="text-xs ml-auto font-normal text-gray-900 mr-4"
          >
            <Edit size={16} />
          </button>
          <PopUpOrientation
            isVisible={showOrientationSheet}
            setIsVisible={setShowOrientationSheet}
          />
        </div>
        <div className="flex gap-2 bg-white p-2 rounded-lg flex-wrap">
          {user?.channelOrientation && user.channelOrientation.length > 0 ? (
            user.channelOrientation.map((category) => (
              <TabTag key={category} label={getOrientationLabel(category)} />
            ))
          ) : (
            <p className="text-xs text-gray-500">Chưa có định hướng</p>
          )}
        </div>
      </div>

      {/* Mã số thuế */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-normal text-gray-900">Mã số thuế</Label>
        </div>
        <div className="relative">
          <Input
            value={taxCodeValue}
            readOnly={!isEditingTaxCode}
            onChange={(e) => setTaxCodeValue(e.target.value)}
            className="pr-10 bg-white rounded-xl border-0 h-12 text-sm"
            placeholder="Chưa có mã số thuế"
            inputMode="numeric"
            autoComplete="off"
          />
          {isEditingTaxCode ? (
            <button
              onClick={handleSaveTaxCode}
              disabled={isSavingTaxCode}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-primary text-white p-2 rounded-lg"
            >
              {isSavingTaxCode ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Check size={16} />
              )}
            </button>
          ) : (
            <button
              onClick={() => setIsEditingTaxCode(true)}
              className="absolute right-4 top-1/2 -translate-y-1/2"
            >
              <Edit size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Identity Card Information - Enhanced UI */}
      <div className="py-2 border-0 shadow-none rounded-xl">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-normal text-gray-900">Căn cước công dân</Label>
          {hasInfo && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2 mr-2"
              onClick={() => setShowSheet(true)}
            >
              <Edit size={16} />
            </Button>
          )}
        </div>
        <div className="p-0">
          {hasInfo ? (
            <div className="space-y-3">
              {/* CCCD Images */}
              {((user?.profiles as any)?.identifyNumberFrontImage ||
                (user?.profiles as any)?.identifyNumberBackImage) && (
                <div className="bg-white px-4 py-4 rounded-xl">
                  <div className="flex gap-3 justify-center">
                    {(user?.profiles as any)?.identifyNumberFrontImage && (
                      <div className="flex-1 max-w-[50% ]">
                        <img
                          src={getImageByNameFormatV2(
                            (user?.profiles as any)?.identifyNumberFrontImage
                          )}
                          alt="Ảnh mặt trước"
                          className="w-full aspect-[1.6] object-cover rounded-lg border border-gray-200"
                        />
                        <p className="text-xs text-gray-600 text-center mt-2">Ảnh mặt trước</p>
                      </div>
                    )}
                    {(user?.profiles as any)?.identifyNumberBackImage && (
                      <div className="flex-1 max-w-[50%]">
                        <img
                          src={getImageByNameFormatV2(
                            (user?.profiles as any)?.identifyNumberBackImage
                          )}
                          alt="Ảnh mặt sau"
                          className="w-full aspect-[1.6] object-cover rounded-lg border border-gray-200"
                        />
                        <p className="text-xs text-gray-600 text-center mt-2">Ảnh mặt sau</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Personal Information Fields */}
              {/* Số định danh cá nhân */}
              <div className="bg-white px-4 py-3 rounded-xl">
                <label className="text-xs text-gray-600 block mb-1">Số căn cước công dân</label>
                <div className="text-sm text-gray-900 font-medium">
                  {user?.profiles?.identifyNumber || "-"}
                </div>
              </div>

              {/* Họ và tên */}
              <div className="bg-white px-4 py-3 rounded-xl">
                <label className="text-xs text-gray-600 block mb-1">Họ và tên trên CCCD</label>
                <div className="text-sm text-gray-900 font-medium">
                  {user?.profiles?.fullName || "-"}
                </div>
              </div>

              {/* Giới tính và Quốc tịch */}
              {/* <div className="bg-white px-4 py-3 rounded-xl">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Giới tính</label>
                    <div className="text-sm text-gray-900 font-medium">
                      {(user?.profiles as any)?.gender || "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Quốc tịch</label>
                    <div className="text-sm text-gray-900 font-medium">
                      {(user?.profiles as any)?.nationality || "-"}
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Quê quán */}
              <div className="bg-white px-4 py-3 rounded-xl">
                <label className="text-xs text-gray-600 block mb-1">Quê quán</label>
                <div className="text-sm text-gray-900 font-medium">
                  {user?.profiles?.placeOfOrigin || "-"}
                </div>
              </div>

              {/* Nơi thường trú */}
              <div className="bg-white px-4 py-3 rounded-xl">
                <label className="text-xs text-gray-600 block mb-1">Nơi thường trú</label>
                <div className="text-sm text-gray-900 font-medium">
                  {user?.profiles?.placeOfResidence || "-"}
                </div>
              </div>

              {/* Ngày, tháng, năm cấp thẻ */}
              <div className="bg-white px-4 py-3 rounded-xl">
                <label className="text-xs text-gray-600 block mb-1">Ngày, tháng, năm cấp thẻ</label>
                <div className="text-sm text-gray-900 font-medium">
                  {user?.profiles?.dateGrantIdentify
                    ? dayjs(user?.profiles?.dateGrantIdentify, "DD/MM/YYYY").format("DD/MM/YYYY")
                    : "-"}
                </div>
              </div>

              {/* Ngày, tháng, năm hết hạn */}
              {/* <div className="bg-white px-4 py-3 rounded-xl">
                <label className="text-xs text-gray-600 block mb-1">Ngày, tháng, năm hết hạn</label>
                <div className="text-sm text-gray-900 font-medium">
                  {(user?.profiles as any)?.dateExpireIdentify
                    ? dayjs((user?.profiles as any)?.dateExpireIdentify).format("DD/MM/YYYY")
                    : "-"}
                </div>
              </div> */}

              {/* Nơi cấp */}
              <div className="bg-white px-4 py-3 rounded-xl">
                <label className="text-xs text-gray-600 block mb-1">Nơi cấp</label>
                <div className="text-sm text-gray-900 font-medium">
                  {user?.profiles?.placeGrantIdentify || "-"}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 px-4 bg-white">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CardIcon size={32} className="text-gray-400" />
              </div>
              <p className="text-gray-900 text-sm font-semibold mb-1">
                Chưa có thông tin CCCD/CMND
              </p>
              <p className="text-gray-500 text-xs mb-6 max-w-xs mx-auto">
                Vui lòng cập nhật thông tin để tham gia các chiến dịch
              </p>
              <Button
                onClick={() => setShowSheet(true)}
                className="h-12 bg-gray-900 hover:bg-gray-800 text-white font-medium px-6 rounded-xl"
              >
                Thêm thông tin CCCD
              </Button>
            </div>
          )}
        </div>
      </div>

      <IdentityCardSheet
        isVisible={showSheet}
        setIsVisible={setShowSheet}
        currentData={
          user?.profiles
            ? {
                identifyNumber: user.profiles.identifyNumber,
                fullName: user.profiles.fullName,
                dateGrantIdentify: user.profiles.dateGrantIdentify
                  ? dayjs(user.profiles.dateGrantIdentify, "DD/MM/YYYY").format("DD/MM/YYYY")
                  : undefined,
                identifyNumberFrontImage: (user.profiles as any)?.identifyNumberFrontImage
                  ? getImageByNameFormatV2((user.profiles as any).identifyNumberFrontImage)
                  : undefined,
                identifyNumberBackImage: (user.profiles as any)?.identifyNumberBackImage
                  ? getImageByNameFormatV2((user.profiles as any).identifyNumberBackImage)
                  : undefined,
                placeGrantIdentify: user.profiles.placeGrantIdentify,
                placeOfOrigin: user.profiles.placeOfOrigin,
                placeOfResidence: user.profiles.placeOfResidence
              }
            : undefined
        }
      />
    </div>
  )
}
